import DOMPurify from "dompurify";

// Type for DOMPurify configuration
interface DOMPurifyConfig {
  ALLOWED_TAGS?: string[];
  ALLOWED_ATTR?: Record<string, string[]>;
  KEEP_CONTENT?: boolean;
  ALLOW_DATA_ATTR?: boolean;
  FORBID_TAGS?: string[];
  FORBID_ATTR?: string[];
}

// Default configuration for rich text content
export const DEFAULT_RICH_TEXT_CONFIG = {
  ALLOWED_TAGS: [
    "p",
    "br",
    "strong",
    "em",
    "u",
    "s",
    "strike",
    "del",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "ul",
    "ol",
    "li",
    "blockquote",
    "a",
  ],
  ALLOWED_ATTR: {
    a: ["href", "title", "target", "rel"],
    "*": ["class"],
  },
  KEEP_CONTENT: true,
  ALLOW_DATA_ATTR: false,
  FORBID_TAGS: [
    "script",
    "style",
    "iframe",
    "object",
    "embed",
    "form",
    "input",
  ],
  FORBID_ATTR: [
    "onclick",
    "onload",
    "onerror",
    "onmouseover",
    "onfocus",
    "onblur",
  ],
};

// Strict configuration for basic text with minimal formatting
export const BASIC_TEXT_CONFIG = {
  ALLOWED_TAGS: ["p", "br", "strong", "em"],
  ALLOWED_ATTR: {},
  KEEP_CONTENT: true,
  ALLOW_DATA_ATTR: false,
};

// Configuration for descriptions with rich text formatting (no links)
export const DESCRIPTION_CONFIG = {
  ALLOWED_TAGS: [
    "p",
    "br",
    "strong",
    "em",
    "u",
    "s",
    "strike",
    "del",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "ul",
    "ol",
    "li",
    "blockquote",
  ],
  ALLOWED_ATTR: {
    "*": ["class"],
  },
  KEEP_CONTENT: true,
  ALLOW_DATA_ATTR: false,
};

/**
 * Sanitizes HTML content using DOMPurify with the specified configuration
 * @param content - The HTML content to sanitize
 * @param config - DOMPurify configuration object (defaults to DEFAULT_RICH_TEXT_CONFIG)
 * @returns Sanitized HTML string
 */
export function sanitizeHtml(
  content: string,
  config: DOMPurifyConfig = DEFAULT_RICH_TEXT_CONFIG
): string {
  if (!content || typeof content !== "string") {
    return "";
  }

  // Additional security: ensure external links have proper security attributes
  const sanitized = DOMPurify.sanitize(content, config) as unknown as string;

  // Post-process to ensure all external links have security attributes
  if (config.ALLOWED_TAGS?.includes("a")) {
    return addSecurityAttributesToLinks(sanitized);
  }

  return sanitized;
}

/**
 * Adds security attributes to external links
 * @param html - HTML content that may contain links
 * @returns HTML with security attributes added to external links
 */
function addSecurityAttributesToLinks(html: string): string {
  // Create a temporary DOM element to parse and modify links
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  const links = tempDiv.querySelectorAll("a[href]");
  links.forEach((link) => {
    const href = link.getAttribute("href");
    if (href && (href.startsWith("http://") || href.startsWith("https://"))) {
      // External link - add security attributes
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener noreferrer");
    }
  });

  return tempDiv.innerHTML;
}

/**
 * Strips all HTML tags and returns plain text
 * @param html - HTML content
 * @returns Plain text content
 */
export function stripHtml(html: string): string {
  if (!html || typeof html !== "string") {
    return "";
  }

  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [],
    KEEP_CONTENT: true,
  });
}

/**
 * Truncates HTML content to a specified length while preserving HTML structure
 * @param html - HTML content to truncate
 * @param maxLength - Maximum length of the plain text content
 * @param suffix - Suffix to add when truncated (default: '...')
 * @returns Truncated HTML content
 */
export function truncateHtml(
  html: string,
  maxLength: number,
  suffix = "..."
): string {
  if (!html || typeof html !== "string") {
    return "";
  }

  const plainText = stripHtml(html);
  if (plainText.length <= maxLength) {
    return html;
  }

  // Find a good truncation point that doesn't break HTML structure
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  let currentLength = 0;
  const walker = document.createTreeWalker(tempDiv, NodeFilter.SHOW_TEXT, null);

  let node: Node | null = walker.nextNode();
  while (node !== null) {
    const textContent = node.textContent || "";
    if (currentLength + textContent.length > maxLength) {
      const remainingLength = maxLength - currentLength;
      node.textContent = textContent.substring(0, remainingLength) + suffix;

      // Remove all subsequent nodes
      let nextNode = walker.nextNode();
      while (nextNode) {
        const nodeToRemove = nextNode;
        nextNode = walker.nextNode();
        nodeToRemove.parentNode?.removeChild(nodeToRemove);
      }
      break;
    }
    currentLength += textContent.length;
    node = walker.nextNode();
  }

  return tempDiv.innerHTML;
}

/**
 * Checks if HTML content is effectively empty (contains only empty paragraphs, line breaks, or whitespace)
 * @param html - HTML content to check
 * @returns true if the content is effectively empty, false otherwise
 */
export function isHtmlContentEmpty(html: string): boolean {
  if (!html || typeof html !== "string") {
    return true;
  }

  // Remove all HTML tags and check if the remaining text content is empty or just whitespace
  const plainText = stripHtml(html).trim();
  if (plainText.length === 0) {
    return true;
  }

  // Check for common empty HTML patterns that rich text editors generate
  const trimmedHtml = html.trim();
  const emptyPatterns = [
    /^<p><br\s*\/?><\/p>$/i,
    /^<p><\/p>$/i,
    /^<br\s*\/?>$/i,
    /^<p>\s*<\/p>$/i,
    /^(<p><br\s*\/?><\/p>\s*)+$/i,
    /^(<p>\s*<\/p>\s*)+$/i,
    /^(<br\s*\/?\s*>)+$/i,
  ];

  return emptyPatterns.some((pattern) => pattern.test(trimmedHtml));
}

/**
 * Validates that HTML content only contains allowed elements
 * @param html - HTML content to validate
 * @param allowedTags - Array of allowed HTML tag names
 * @returns Object with isValid boolean and array of invalid tags found
 */
export function validateHtmlContent(
  html: string,
  allowedTags: string[] = DEFAULT_RICH_TEXT_CONFIG.ALLOWED_TAGS || []
): { isValid: boolean; invalidTags: string[] } {
  if (!html || typeof html !== "string") {
    return { isValid: true, invalidTags: [] };
  }

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  const allElements = tempDiv.querySelectorAll("*");
  const invalidTags: string[] = [];

  allElements.forEach((element) => {
    const tagName = element.tagName.toLowerCase();
    if (!allowedTags.includes(tagName)) {
      if (!invalidTags.includes(tagName)) {
        invalidTags.push(tagName);
      }
    }
  });

  return {
    isValid: invalidTags.length === 0,
    invalidTags,
  };
}
